import { Outlet, matchPath, useLocation } from "react-router";
import { Loader } from "@f2m3/shared-frontend-ui";
import TopNav from "../../components/layout/TopNav";
import ImpersonationBanner from "../../components/layout/ImpersonationBanner";
import Footer from "../../components/layout/Footer";
import ErrorBoundary from "../../components/common/ErrorBoundary";
import ScrollToTop from "../../components/common/ScrollToTop";
import ShopClosedPage from "../../components/common/ShopClosedPage";
import ShopStateNotice from "../../components/common/ShopStateNotice";
import ConsentManager from "../../consent/ConsentManager";
import { OPEN_LINK_PATH_PREFIX } from "../../api/albumOpenLink";
import { useShopState } from "../../hooks/useShopState";
import { useStaticPages } from "../../api/queryHooks/account";

// Routes reachable even while the shop is closed — staff login/auth flows
// and static pages (e.g. Terms/Privacy) stay available.
const CLOSED_ALLOWED_PATHS = [
  '/login',
  '/auth/transfer',
  '/forgot-password',
  '/reset-password',
  '/page/:slug',
  // Cookie information stays reachable while the shop is closed: the banner
  // and the footer link to it, and a visitor must always be able to read what
  // the site stores about them.
  '/cookies',
  // Unsubscribing from album notifications must work even while the shop is
  // closed: the person asked us to stop emailing them.
  '/unsubscribe/:token',
];

// Routes a customer of a shop that isn't taking orders gets the notice in
// place of: album browsing (the backend refuses it, so album links in old
// emails and album QR codes land here too) and the cart and checkout. Their
// orders, downloads, messages and profile keep working.
const PAUSED_BLOCKED_PATHS = [
  '/albums',
  '/albums/:albumId',
  '/albums/:albumId/:picId',
  `/${OPEN_LINK_PATH_PREFIX}/:code`,
  '/order/*',
];

const matchesAny = (patterns: string[], pathname: string): boolean =>
  patterns.some((p) => matchPath(p, pathname));

// The document title lives in useShopHead (called from ThemeProvider) so admin
// routes get it too — it used to be set here, which left /admin/* on whatever
// title was last applied.
const MainLayout = () => {
  const location = useLocation();
  const { closedForMe, pausedForMe, isPending: shopStateLoading } = useShopState();
  // A closed shop's front page is its static homepage, when it has one
  // (static pages stay public). Without one, "/" only redirects, so the
  // visitor gets ShopClosedPage there. Same check as TopNav's Home link.
  const { data: staticPages, isPending: staticPagesPending } = useStaticPages();
  const hasHomepage = staticPages?.some((page) => page.homepage_page) ?? false;
  const onHome = location.pathname === '/';
  const shopStatePending = shopStateLoading || (closedForMe && onHome && staticPagesPending);
  const closedOut =
    closedForMe && !matchesAny(CLOSED_ALLOWED_PATHS, location.pathname) && !(onHome && hasHomepage);
  const pausedOut = pausedForMe && matchesAny(PAUSED_BLOCKED_PATHS, location.pathname);

  return (
    <div id="main-layout">
      <ScrollToTop />
      <ConsentManager />
      {/* A blocked page shows the notice in its place instead. */}
      {!shopStatePending && !pausedOut && <ShopStateNotice />}
      <div className="impersonation-banner-sticky">
        <ImpersonationBanner />
        <TopNav />
      </div>
      <div className="container">
        <ErrorBoundary resetKey={location.pathname}>
          {shopStatePending ? (
            <Loader />
          ) : closedOut ? (
            <ShopClosedPage />
          ) : pausedOut ? (
            <ShopStateNotice inline />
          ) : (
            <Outlet />
          )}
        </ErrorBoundary>
      </div>
      <Footer />
    </div>
  );
};

export default MainLayout;
